import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useEffect, useState, useCallback, memo} from 'react';
import {useNavigate} from 'extensions/navigation';
import {usePopup} from 'extensions/viewport/usePopup';
import {useSelector, useDispatch} from 'react-redux';
import {useWindowDimensions, StyleSheet, View, Text, Image, Pressable, GestureResponderEvent} from 'react-native';
import {useHotkeys} from 'interface/hooks/useHotkeys';
import {useWelcome} from 'interface/hooks/useWelcome';
import {useMenuFolder} from 'interface/hooks/useMenuFolder';
import {FolderHeader} from 'interface/stacks/FolderHeader';
import {FolderToolbar} from 'interface/stacks/FolderToolbar';
import {FolderBreadcrumbs} from 'interface/stacks/FolderBreadcrumbs';
import {FileList} from 'interface/stacks/FileList';
import {Splash} from 'interface/stacks/Splash';
import {Loading} from 'interface/base/Loading';
import {Mouse} from 'interface/base/Mouse';
import {isWeb, isUpsellDeniedPlatform} from 'utils/platform';
import {testBit} from 'utils/common/data';
import device from 'extensions/device';
import images from 'config/images';
import {theme} from 'config/themes';
import {colors, vectors} from 'config/styles';
import {FilesItemFlag} from 'store/slices/files/types';
import {FolderTree} from 'interface/components/FolderTree';
import {load, isFolderSpecial, openPage} from 'utils/mediafire';
import {selectors} from 'store/slices/files';
import {
  STORAGE_OVER_THRESHOLD_PERCENT,
  STORAGE_REMIND_THRESHOLD_PERCENT,
  STORAGE_WARN_THRESHOLD_PERCENT,
} from 'interface/hooks/useModalStorageUpsell';
import {useStorageBannerTest} from 'interface/hooks/useStorageBannerTest';
import * as app from 'store/slices/app';

export interface FolderContentProps {
  id: string,
}

export const FolderContents = memo((props: FolderContentProps) => {
  const {t} = useLingui();
  const popup = usePopup();
  const [loaded, setLoaded] = useState(false);
  const isLoggedIn = useSelector(app.selectors.isLoggedIn);
  const isPremium = useSelector(app.selectors.isPremium);
  const limits = useSelector(app.selectors.getLimits);
  const isSearch = useSelector(selectors.isSearch);
  const filter = useSelector(selectors.getFilter);
  const sort = useSelector(selectors.getSort);
  const items = useSelector(selectors.getItems);
  const query = useSelector(selectors.getQuery);
  const following = useSelector(selectors.getView('following'));
  const recent = useSelector(selectors.getView('recent'));
  const search = useSelector(selectors.getView('search'));
  const folder = useSelector(selectors.getItem(props.id));
  const view = useSelector(selectors.getView(props.id));
  const screen = useWindowDimensions();
  const isSpecial = isFolderSpecial(props.id);
  const menuFolder = useMenuFolder(props.id, folder?.name);

  const isTrash = props.id === 'trash';
  const trashRetentionDays = isPremium ? 180 : 30;
  const storagePercent = limits && limits.storage.total > 0
    ? (limits.storage.used / limits.storage.total) * 100
    : 0;
  const canUpsell = !isPremium && !isUpsellDeniedPlatform();
  const canShowStorageBanner = !isPremium && !isTrash;
  const bannerTest = useStorageBannerTest();
  const showStorageOver = bannerTest === 'over'
    || (!bannerTest && canShowStorageBanner
      && storagePercent > STORAGE_OVER_THRESHOLD_PERCENT);
  const showStorageCritical = bannerTest === 'critical'
    || (!bannerTest && canShowStorageBanner
      && !showStorageOver
      && storagePercent >= STORAGE_REMIND_THRESHOLD_PERCENT);
  const showStorageWarning = bannerTest === 'warning'
    || (!bannerTest && canShowStorageBanner
      && !showStorageOver
      && !showStorageCritical
      && storagePercent >= STORAGE_WARN_THRESHOLD_PERCENT);
  const openUpgrade = useCallback(
    () => openPage('upgrade/?utm_source=banner&utm_medium=website&utm_content=upgrade&utm_campaign=storage_banner'),
    [],
  );
  const isRecent = props.id === 'recent';
  const isFollowing = props.id === 'following';
  const isShared = !isSpecial && folder && !testBit(folder.flag, FilesItemFlag.isOwned);
  const isOwned = isLoggedIn && !isShared;
  const isDarkMode = device.isDarkMode();
  const isMinimal = screen.width < 600;
  const isLargeScreen = screen.width > 1120 && screen.height > 540;
  const isLoadingSearch = isSearch && !search && query;

  const sizeActual = view
    ? view.files.length + view.folders.length
    : 0;

  const sizeExpected = folder
    ? (filter.type === 'all' || !view ? folder.files : view.files.length)
    + (filter.type === 'all' || !view ? folder.folders : view.folders.length)
    : 0;

  const isLoadingFolder = isRecent
    ? !recent : isFollowing ? !following
    : (!folder || sizeActual === 0 && sizeExpected > 0);

  const hasHeader = !isOwned && !isTrash && !isLoadingFolder;
  const hasSpinner = isLoadingFolder || isLoadingSearch;

  const hasEmptyFolder = !isLoadingFolder &&
    (folder || (
      (isRecent || isFollowing || isShared)
      && sizeActual === 0
    ))
    && sizeExpected === 0
    && !query;
  
  const hasEmptySearch = isSearch
    && !isLoadingSearch
    && search
    && (search.files.length === 0 && search.folders.length === 0);
  
  const hasEmptyUploadTip = !isDarkMode
    && isLargeScreen
    && !isTrash
    && !isRecent
    && !isFollowing
    && !isShared;
  
  const hasListFolder = !isLoadingFolder
    && !hasEmptyFolder
    && !query;

  const hasListSearch = !isTrash
    && !isFollowing
    && !isLoadingFolder
    && !hasEmptySearch
    && !hasListFolder
    && search;
  
  const classes = {
    content: [
      styles.content,
      isMinimal && styles.minimal,
    ],
    uploadTip: [
      styles.uploadTip,
      hasEmptyUploadTip && styles.dragDrop,
    ],
    header: [
      styles.header,
      !hasEmptyUploadTip && styles.dragDropHeader,
    ],
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useHotkeys(props.id);
  useWelcome(isLoggedIn);

  // Load folder contents (this handles the folder access redirects)
  useEffect(() => {
    const isInit = !folder && props.id === 'myfiles';
    load(navigate, dispatch, props.id, isInit, sort, filter)
      .then(() => setLoaded(true));
    if (isLoggedIn) {
      load(navigate, dispatch, 'trash');
    }
  }, [props.id, navigate, dispatch, sort, filter]);

  if (isSpecial && !isLoggedIn) {
    return <Splash/>;
  }

  return (
    <View ref={menuFolder.reference} style={styles.root}>
      <Mouse onContextMenu={
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (isTrash) return;
          popup.setPosition({x: event.pageX, y: event.pageY});
          popup.setRef(menuFolder.reference);
          menuFolder.open();
        }
      }>
        <View style={styles.contentContainer}>
          <FolderToolbar id={props.id} loaded={loaded}/>
          {isTrash && !bannerTest &&
            <View style={styles.trashNotice}>
              <Svg viewBox="0 0 24 24" width={18} height={18} style={styles.bannerIcon}>
                <Path d={vectors.icons.danger} fill={colors.secondary.yellow}/>
              </Svg>
              <Text style={styles.trashNoticeText}>
                {t`Files and folders in the trash folder are automatically deleted after ${trashRetentionDays} days.`}
              </Text>
            </View>
          }
          {showStorageOver &&
            <View style={styles.criticalNotice}>
              <Svg viewBox="0 0 24 24" width={18} height={18} style={styles.bannerIcon}>
                <Path d={vectors.icons.danger} fill={colors.secondary.brightRed}/>
              </Svg>
              <Text style={styles.criticalNoticeText}>
                {canUpsell
                  ? t`You're out of storage. Upgrade to get more space.`
                  : t`You're out of storage.`}
              </Text>
              {canUpsell &&
                <Pressable onPress={openUpgrade} style={styles.bannerButtonCritical}>
                  <Text style={styles.bannerButtonText}>{t`Upgrade`}</Text>
                </Pressable>
              }
            </View>
          }
          {showStorageCritical &&
            <View style={styles.criticalNotice}>
              <Svg viewBox="0 0 24 24" width={18} height={18} style={styles.bannerIcon}>
                <Path d={vectors.icons.danger} fill={colors.secondary.brightRed}/>
              </Svg>
              <Text style={styles.criticalNoticeText}>
                {canUpsell
                  ? t`You're almost out of storage. Upgrade to get more space.`
                  : t`You're almost out of storage.`}
              </Text>
              {canUpsell &&
                <Pressable onPress={openUpgrade} style={styles.bannerButtonCritical}>
                  <Text style={styles.bannerButtonText}>{t`Upgrade`}</Text>
                </Pressable>
              }
            </View>
          }
          {showStorageWarning &&
            <View style={styles.warningNotice}>
              <Svg viewBox="0 0 20 20" width={18} height={18} style={styles.bannerIcon}>
                <Path d={vectors.icons.danger} fill={colors.secondary.yellow}/>
              </Svg>
              <Text style={styles.trashNoticeText}>
                {canUpsell
                  ? t`Your storage is filling up. Upgrade for more space.`
                  : t`Your storage is filling up.`}
              </Text>
              {canUpsell &&
                <Pressable onPress={openUpgrade} style={styles.bannerButtonWarning}>
                  <Text style={styles.bannerButtonText}>{t`Upgrade`}</Text>
                </Pressable>
              }
            </View>
          }
          <View style={styles.mainContent}>
            {isOwned && !isMinimal && isWeb() &&
              <FolderTree/>
            }
            <View style={classes.content}>
              {hasHeader &&
                <FolderHeader id={props.id}/>
              }
              {isOwned &&
                <FolderBreadcrumbs id={props.id} search={query}/>
              }
              <View style={styles.lists}>
                {hasListFolder &&
                  <View style={styles.list}>
                    <FileList
                      view={view}
                      items={items}
                      options={{
                        hideHeader: isRecent || isShared,
                        hideFolders: isRecent,
                        hideExtraIcons: isTrash,
                        disableOpen: isTrash,
                        disableDrag: isTrash || isFollowing,
                      }}
                    />
                  </View>
                }
                {hasListSearch && !hasEmptyFolder &&
                  <View style={styles.list}>
                    <FileList
                      view={search}
                      items={items}
                      options={{
                        linkParent: true,
                      }}
                    />
                  </View>
                }
                {hasEmptySearch &&
                  <View style={styles.center}>
                    <View style={styles.searchEmpty}>
                      <Svg viewBox={'0 0 200 200'} width={200} height={200}>
                        <Path d={vectors.artwork.searchNotFound[0]} fill="#8AC5FF"/>
                        <Path d={vectors.artwork.searchNotFound[1]} fill="#D2E9FF"/>
                        <Path d={vectors.artwork.searchNotFound[2]} fill="#99CCFF"/>
                        <Path d={vectors.artwork.searchNotFound[3]} fill="#A6D3FF"/>
                        <Path d={vectors.artwork.searchNotFound[4]} fill="#C0E0FF"/>
                        <Path d={vectors.artwork.searchNotFound[5]} fill="#D2E9FF"/>
                        <Path d={vectors.artwork.searchNotFound[6]} fill="#0070F0"/>
                        <Path d={vectors.artwork.searchNotFound[7]} fill="#80C0FF"/>
                      </Svg>
                      <Text style={styles.header}>
                        {t`No results found`}
                      </Text>
                      <Text style={styles.footer}>
                        {t`Sorry, we looked but didn't find anything.`}
                      </Text>
                    </View>
                  </View>
                }
                {hasEmptyFolder &&
                  <View style={styles.center}>
                    <View style={classes.uploadTip}>
                      {hasEmptyUploadTip &&
                        <Image style={styles.uploadTipBg} source={{uri: images.emptyBackground}}/>
                      }
                      {hasEmptyUploadTip &&
                        <Image style={styles.uploadTipDrag} source={{uri: images.emptyDrag}}/>
                      }
                      <Text style={classes.header}>
                        {isRecent
                          ? t`No recent files`
                          : isTrash
                              ? t`Trash is empty`
                              : isFollowing
                                ? t`You are not following anything`
                                : t`This folder is empty`
                        }
                      </Text>
                      {hasEmptyUploadTip &&
                        <Text style={styles.footer}>
                          {t`Upload files or folders by clicking the upload button, or drag &amp; drop from your device.`}
                        </Text>
                      }
                    </View>
                  </View>
                }
                {hasSpinner &&
                  <View style={styles.loading}>
                    <Loading/>
                  </View>
                }
              </View>
            </View>
          </View>
        </View>
      </Mouse>
    </View>
  )
});

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.files.fill,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
  },
  trashNotice: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: colors.secondary.tan,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  criticalNotice: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: "#ffd3c7",
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  warningNotice: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: colors.secondary.tan,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerIcon: {
    marginRight: 8,
  },
  trashNoticeText: {
    color: colors.neutral.dark,
    fontSize: 13,
    fontWeight: '500',
    textAlign: 'center',
  },
  criticalNoticeText: {
    color: colors.neutral.dark,
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  bannerButtonCritical: {
    backgroundColor: colors.primary.brand,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginLeft: 12,
  },
  bannerButtonWarning: {
    backgroundColor: colors.primary.brand,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginLeft: 12,
  },
  bannerButtonText: {
    color: colors.neutral.white,
    fontSize: 13,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  minimal: {
    marginHorizontal: 0,
  },
  lists: {
    flex: 1,
    flexDirection: 'row',
  },
  list: {
    flex: 1,
    margin: 0,
    flexDirection: 'row',
  },
  header: {
    textAlign: 'center',
    color: colors.neutral.light,
    fontSize: 26,
  },
  footer: {
    textAlign: 'center',
    color: colors.neutral.mid,
    fontSize: 14,
    marginTop: 16,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchEmpty: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    flex: 1,
  },
  dragDrop: {
    width: 640,
    height: 360,
    padding: 36,
    borderRadius: 5,
    borderWidth: 5,
    borderStyle: 'dashed',
    borderColor: '#E6E6E6',
    position: 'relative',
  },
  dragDropHeader: {
    textAlign: 'center',
    color: colors.neutral.light,
    fontSize: 26,
  },
  uploadTip: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  uploadTipBg: {
    width: 559,
    height: 150,
    marginBottom: 26,
  },
  uploadTipDrag: {
    position: 'absolute',
    top: 100,
    left: 300,
    width: 58,
    height: 46,
    opacity: 1,
  },
});
